import React from 'react';

import {
  Box,
  Button,
  Chip,
  Divider,
  ModalBox,
  PageHeader,
  Typography,
} from 'components';
import { useNotifications } from 'hooks';
import { Copy, InfoCircle } from 'iconsax-react';

import { copyToClipboard, useSelector, useTranslation } from 'third-party';

import { DEFAULT_TIMEZONE } from 'constants/common';

import { settingSelectors } from 'selectors';

import { formatDateTimeWithTimezone, moneyFormatter } from 'utils/app';

import { APIAudit } from 'types/api';
import { APIEntityType, APIEventType, APISettingName } from 'types/backend';

import { palette } from 'styles/theme';

type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

const EVENT_CHIP_COLOR: Record<APIEventType, ChipColor> = {
  [APIEventType.PaymentCreated]: 'success',
  [APIEventType.PayeeCreated]: 'success',
  [APIEventType.BankCreated]: 'success',
  [APIEventType.UserCreated]: 'success',
  [APIEventType.UserRegistered]: 'success',
  [APIEventType.SettingsCreated]: 'success',

  [APIEventType.PaymentDeleted]: 'error',
  [APIEventType.PaymentVoided]: 'error',
  [APIEventType.PayeeDeleted]: 'error',
  [APIEventType.BankDeleted]: 'error',
  [APIEventType.UserDeleted]: 'error',
  [APIEventType.UserRemovedFromOrganization]: 'error',

  [APIEventType.PaymentUpdated]: 'info',
  [APIEventType.PayeeUpdated]: 'info',
  [APIEventType.BankUpdated]: 'info',
  [APIEventType.UserUpdated]: 'info',
  [APIEventType.UserUpdatedOwnProfile]: 'info',
  [APIEventType.UserRightsUpdated]: 'info',
  [APIEventType.SettingsUpdated]: 'info',

  [APIEventType.PaymentSent]: 'primary',
  [APIEventType.PaymentApproved]: 'primary',
  [APIEventType.PaymentResented]: 'primary',
  [APIEventType.UserAddedToOrganization]: 'primary',

  [APIEventType.ApiClientTokenCreated]: 'success',
  [APIEventType.ApiClientTokenEnabled]: 'primary',
  [APIEventType.ApiClientTokenDisabled]: 'warning',
  [APIEventType.ApiClientTokenDeleted]: 'error',

  [APIEventType.BlankCheckGenerated]: 'primary',

  [APIEventType.BillingChecksPurchased]: 'success',
  [APIEventType.BillingCheckPurchaseStarted]: 'primary',
  [APIEventType.BillingPlanChanged]: 'info',
  [APIEventType.BillingCreditUpdated]: 'info',
  [APIEventType.BillingSubscriptionStatusChanged]: 'warning',
  [APIEventType.BillingPlanCanceled]: 'error',
};

export const getEventChipColor = (eventType: APIEventType): ChipColor =>
  EVENT_CHIP_COLOR[eventType] ?? 'default';

type DetailFieldProps = {
  label: string;
  value: string;
};

const DetailField: React.FC<DetailFieldProps> = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      display="block"
      gutterBottom
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ wordBreak: 'break-all', minHeight: '1.25rem' }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

type Props = {
  audit: APIAudit;
  onClose: () => void;
};

type FieldDef = {
  labelKey: string;
  valueMapper?: (val: string, t: (key: any) => string) => string;
};

const ENTITY_FIELD_DEFS: Partial<Record<APIEntityType, FieldDef[]>> = {
  [APIEntityType.Payee]: [
    { labelKey: 'auditList.detail.name' },
    { labelKey: 'auditList.detail.email' },
    { labelKey: 'auditList.detail.phone' },
    { labelKey: 'auditList.detail.status' },
  ],
  [APIEntityType.Payment]: [
    {
      labelKey: 'auditList.detail.amount',
      valueMapper: val => moneyFormatter(val),
    },
    { labelKey: 'auditList.detail.payee' },
    { labelKey: 'auditList.detail.status' },
  ],
  [APIEntityType.Bank]: [
    { labelKey: 'auditList.detail.bankName' },
    { labelKey: 'auditList.detail.accountName' },
    { labelKey: 'auditList.detail.routing' },
    { labelKey: 'auditList.detail.account' },
    { labelKey: 'auditList.detail.companyName' },
  ],
  [APIEntityType.User]: [
    { labelKey: 'auditList.detail.firstName' },
    { labelKey: 'auditList.detail.lastName' },
    { labelKey: 'auditList.detail.email' },
  ],
  [APIEntityType.Settings]: [
    {
      labelKey: 'auditList.detail.setting',
      valueMapper: (val, t) => (val ? t(('settings.' + val) as any) : ''),
    },
  ],
  [APIEntityType.ApiClientToken]: [
    { labelKey: 'auditList.detail.name' },
    { labelKey: 'auditList.detail.isEnabled' },
  ],
  [APIEntityType.BlankCheckRange]: [
    { labelKey: 'auditList.detail.bankName' },
    { labelKey: 'auditList.detail.checkFrom' },
    { labelKey: 'auditList.detail.checkTo' },
    { labelKey: 'auditList.detail.signature' },
    { labelKey: 'auditList.detail.printType' },
  ],
};

function buildDataRows(
  entityType: APIEntityType,
  data: string[],
  t: (key: any) => string,
): { label: string; value: string }[] {
  // Billing entries label themselves — "Plan: Pro → Free" — because which fields
  // an entry carries depends on what actually changed, so there is no fixed set
  // of positions to name.
  if (entityType === APIEntityType.Billing) {
    return data.map((entry, i) => {
      const separator = entry.indexOf(': ');

      return separator === -1
        ? { label: `${t('auditList.detail.field')} ${i + 1}`, value: entry }
        : {
            label: entry.slice(0, separator),
            value: entry.slice(separator + 2),
          };
    });
  }

  const defs = ENTITY_FIELD_DEFS[entityType];

  if (defs) {
    return defs.map((def, i) => ({
      label: t(def.labelKey as any),
      value: def.valueMapper
        ? def.valueMapper(data[i] ?? '', t)
        : data[i] ?? '',
    }));
  }

  return data.map((val, i) => ({
    label: `${t('auditList.detail.field')} ${i + 1}`,
    value: val,
  }));
}

export const AuditDetailModal: React.FC<Props> = ({ audit, onClose }) => {
  const { t } = useTranslation();
  const { success } = useNotifications();
  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );
  const data = audit.data ?? [];

  const dataRows = buildDataRows(audit.entityType, data, t);

  const nonEmptyRows = dataRows.filter(r => r.value);

  const handleCopy = () => {
    const lines: string[] = [
      `${t('auditList.detail.title')}`,
      `${t('auditList.column.createdAt')}: ${formatDateTimeWithTimezone(
        audit.createdAt,
        timezoneValue,
      )}`,
      `${t('auditList.column.createdBy')}: ${
        audit.createdBy
          ? `${audit.createdBy.firstName} ${audit.createdBy.lastName}${
              audit.createdByToken
                ? ` via api token "${audit.createdByToken.name}"`
                : ''
            }`
          : '—'
      }`,
      `${t('auditList.column.entityId')}: ${audit.entityId}`,
      `${t('auditList.column.eventType')}: ${t(
        ('audit.status.' + audit.eventType) as any,
      )}`,
      `${t('auditList.column.entityType')}: ${t(
        ('audit.entityType.' + audit.entityType) as any,
      )}`,
    ];
    if (nonEmptyRows.length > 0) {
      lines.push('');
      lines.push(t('auditList.detail.eventData'));
      nonEmptyRows.forEach(r => lines.push(`${r.label}: ${r.value}`));
    }
    copyToClipboard(lines.join('\n'));
    success(t('common.copyToClipboard'));
  };

  return (
    <ModalBox width="30rem" height="32rem" onClose={onClose}>
      <Box padding="1.5rem">
        <PageHeader
          header={t('auditList.detail.title')}
          subHeader={`${t(
            ('audit.entityType.' + audit.entityType) as any,
          )} · ${t(('audit.status.' + audit.eventType) as any)}`}
        >
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Copy variant="TwoTone" size="24" />}
            onClick={handleCopy}
          >
            {t('common.copy')}
          </Button>
        </PageHeader>

        <Box mt={2}>
          {/* Event type chip */}
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            <Chip
              label={t(('audit.status.' + audit.eventType) as any)}
              color={getEventChipColor(audit.eventType)}
              size="small"
            />
            <Chip
              label={t(('audit.entityType.' + audit.entityType) as any)}
              variant="outlined"
              size="small"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Core metadata */}
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            gap="0.5rem 1.5rem"
            mb={1}
          >
            <DetailField
              label={t('auditList.column.createdAt')}
              value={formatDateTimeWithTimezone(audit.createdAt, timezoneValue)}
            />
            <DetailField
              label={t('auditList.column.createdBy')}
              value={
                audit.createdBy
                  ? `${audit.createdBy.firstName} ${audit.createdBy.lastName}${
                      audit.createdByToken
                        ? ` via api token "${audit.createdByToken.name}"`
                        : ''
                    }`
                  : '—'
              }
            />
          </Box>
          <DetailField
            label={t('auditList.column.entityId')}
            value={audit.entityId}
          />

          {/* Entity-specific data */}
          {nonEmptyRows.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
                <InfoCircle
                  variant="TwoTone"
                  size="20"
                  color={palette.primary900}
                />
                <Typography variant="h2">
                  {t('auditList.detail.eventData')}
                </Typography>
              </Box>
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap="0.5rem 1.5rem"
              >
                {nonEmptyRows.map(row => (
                  <DetailField
                    key={row.label}
                    label={row.label}
                    value={row.value}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ModalBox>
  );
};
